import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Timer, Save, Sparkles, Phone, MapPin, Users, Bus, Heart, ClipboardCheck, MessageSquare, AlertTriangle, Loader2, CalendarCheck, XCircle } from "lucide-react";
import api, { URGENCIES } from "@/lib/api";
import { CallControls } from "@/components/CallControls";
import { useQualificationTags } from "@/hooks/useQualificationTags";

const TIMER_SECONDS = 5 * 60; // 5 minutos
const RELATIONSHIPS = ["Próprio", "Filho(a)", "Cônjuge", "Neto(a)", "Sobrinho(a)", "Amigo(a)", "Outro"];
const TREATMENTS = ["Sr.", "Sra.", "Sra. D.", "Dr.", "Dra.", "Prof.", "Prof.ª", "Eng.", "Eng.ª", "Padre", "Madre"];

const fmtTime = (s) => {
  const m = Math.floor(Math.max(0, s) / 60);
  const r = Math.max(0, s) % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
};

const inp = "w-full h-12 bg-muted/40 border border-border rounded-full px-5 text-[15px] text-foreground placeholder-[#94A3B8] focus:outline-none focus:border-primary focus:bg-card transition-colors";
const ta = "w-full bg-muted/40 border border-border rounded-xl px-4 py-3 text-[15px] text-foreground placeholder-[#94A3B8] focus:outline-none focus:border-primary focus:bg-card transition-colors";
const pill = (active) => `text-xs px-4 py-2 rounded-full border transition-colors ${active
  ? "bg-foreground text-background border-[#0F172A]"
  : "bg-card text-[#374151] border-border hover:border-primary"}`;

/** A section of the script with a "read this aloud" prompt and one or more inputs. */
function ScriptBlock({ index, total, kicker, question, hint, mandatory, children }) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-6 lg:px-8 py-4 border-b border-border flex items-center gap-3 bg-muted/40">
        <div className="text-[10px] uppercase tracking-[0.24em] text-foreground font-semibold">Etapa {index}/{total}</div>
        <div className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground font-semibold flex-1">{kicker}</div>
        {mandatory && (
          <span className="text-[9px] uppercase tracking-[0.16em] font-semibold text-[hsl(var(--danger))] bg-[hsl(var(--warning-bg))] border border-[#DC2626]/30 px-2 py-0.5 rounded-full">Obrigatório</span>
        )}
      </div>
      <div className="p-6 lg:p-8 space-y-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold mb-2 flex items-center gap-1.5">
            <MessageSquare className="w-3 h-3" /> Diga ao cliente
          </div>
          <blockquote className="border-l-2 border-primary pl-4 text-[15px] leading-relaxed text-foreground font-display italic">
            {question}
          </blockquote>
          {hint && <div className="text-[12px] text-muted-foreground mt-2 pl-4">💡 {hint}</div>}
        </div>
        <div className="space-y-3">{children}</div>
      </div>
    </div>
  );
}

const Label = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.18em] text-foreground font-semibold mb-1">{children}</label>
);

const Field = ({ label, children }) => (
  <div>
    {label && <Label>{label}</Label>}
    {children}
  </div>
);

/** Renders one custom field row based on its `type`. Reads/writes `data.custom_data`. */
function CustomFieldRow({ field, value, onChange }) {
  const setVal = (v) => onChange(field.key, v);
  const cur = value ?? "";
  if (field.type === "textarea") {
    return <textarea data-testid={`cf-${field.key}`} rows={3} className={ta} placeholder={field.placeholder||""} value={cur} onChange={(e)=>setVal(e.target.value)} />;
  }
  if (field.type === "number") {
    return <input data-testid={`cf-${field.key}`} type="number" className={inp} placeholder={field.placeholder||""} value={cur} onChange={(e)=>setVal(e.target.value === "" ? "" : Number(e.target.value))} />;
  }
  if (field.type === "email") {
    return <input data-testid={`cf-${field.key}`} type="email" className={inp} placeholder={field.placeholder||""} value={cur} onChange={(e)=>setVal(e.target.value)} />;
  }
  if (field.type === "phone" || field.type === "tel") {
    return <input data-testid={`cf-${field.key}`} type="tel" className={inp} placeholder={field.placeholder||""} value={cur} onChange={(e)=>setVal(e.target.value)} />;
  }
  if (field.type === "date") {
    return <input data-testid={`cf-${field.key}`} type="date" className={inp} value={cur} onChange={(e)=>setVal(e.target.value)} />;
  }
  if (field.type === "time") {
    return <input data-testid={`cf-${field.key}`} type="time" className={inp} value={cur} onChange={(e)=>setVal(e.target.value)} />;
  }
  if (field.type === "select") {
    return (
      <select data-testid={`cf-${field.key}`} className={inp} value={cur} onChange={(e)=>setVal(e.target.value)}>
        <option value="">—</option>
        {(field.options||[]).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }
  if (field.type === "radio" || field.type === "yesno") {
    const opts = field.type === "yesno" ? [{value:"yes",label:"Sim"},{value:"no",label:"Não"}] : (field.options||[]);
    return (
      <div className="flex flex-wrap gap-2" data-testid={`cf-${field.key}`}>
        {opts.map((o) => (
          <button key={o.value} type="button"
            data-testid={`cf-${field.key}-${o.value}`}
            onClick={()=>setVal(o.value)}
            className={`text-xs px-3.5 py-2 rounded-full border transition-colors ${cur === o.value ? "bg-foreground text-background border-[#0F172A]" : "bg-card text-[#374151] border-border hover:border-primary"}`}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  if (field.type === "checkboxes" || field.type === "chips") {
    const arr = Array.isArray(cur) ? cur : [];
    const toggle = (v) => setVal(arr.includes(v) ? arr.filter(x=>x!==v) : [...arr, v]);
    return (
      <div className="flex flex-wrap gap-2" data-testid={`cf-${field.key}`}>
        {(field.options||[]).map((o) => (
          <button key={o.value} type="button"
            data-testid={`cf-${field.key}-${o.value}`}
            onClick={()=>toggle(o.value)}
            className={`text-xs px-3.5 py-2 rounded-full border transition-colors ${arr.includes(o.value) ? "bg-foreground text-background border-[#0F172A]" : "bg-card text-[#374151] border-border hover:border-primary"}`}>
            {o.label}
          </button>
        ))}
      </div>
    );
  }
  // default: text
  return <input data-testid={`cf-${field.key}`} className={inp} placeholder={field.placeholder||""} value={cur} onChange={(e)=>setVal(e.target.value)} />;
}

export default function LeadScript() {
  const nav = useNavigate();
  const { id: existingLeadId } = useParams();
  const [step, setStep] = useState(0);
  const [remaining, setRemaining] = useState(TIMER_SECONDS);
  const [saving, setSaving] = useState(false);
  const [expiredWarned, setExpiredWarned] = useState(false);
  const [loadingLead, setLoadingLead] = useState(!!existingLeadId);
  // Dynamic tag lists (admin-editable in /settings/qualification-tags).
  const { serviceTypes: SERVICE_TAGS, needs: NEEDS_TAGS } = useQualificationTags();
  // Lead "cru" (para os CallControls) — mantido em paralelo com `data`
  // porque este último é só a versão flatten do formulário. Ao reload
  // (após iniciar/terminar chamada) só refrescamos este objecto para
  // que o cronómetro e o contador de chamadas actualizem sem perder
  // aquilo que o utilizador já preencheu no ecrã.
  const [rawLead, setRawLead] = useState(null);
  const startedAt = useRef(Date.now());

  // 20/02/2026 — Postal code auto-fill state. Same pattern as
  // LeadNew.jsx — hit ``/api/postal-code/{cp}`` and populate
  // ``municipality`` + ``district`` when the operator finishes typing.
  const [postalLookup, setPostalLookup] = useState({ loading: false, error: "", ok: false });
  const _normaliseCp = (v) => {
    const digits = (v || "").replace(/\D/g, "").slice(0, 7);
    if (digits.length <= 4) return digits;
    return `${digits.slice(0, 4)}-${digits.slice(4)}`;
  };
  const lookupPostal = async (raw) => {
    const cp = _normaliseCp(raw);
    if (cp.length < 8) {
      setPostalLookup({ loading: false, error: "", ok: false });
      return;
    }
    setPostalLookup({ loading: true, error: "", ok: false });
    try {
      const r = await api.get(`/postal-code/${cp}`);
      const muni = r.data?.municipality || "";
      const dist = r.data?.district || "";
      setData((d) => ({
        ...d,
        location: {
          ...d.location,
          postal_code: cp,
          municipality: d.location.municipality || muni,
          district: d.location.district || dist,
        },
      }));
      setPostalLookup({ loading: false, error: "", ok: !!(muni || dist) });
    } catch (e) {
      setPostalLookup({
        loading: false,
        error: e.response?.data?.detail || "Código postal não encontrado",
        ok: false,
      });
    }
  };

  const [data, setData] = useState({
    contact: { name: "", phone: "", email: "", relationship: "", source: "Telefone", language: "pt", treatment: "", is_self: false },
    location: { postal_code: "", municipality: "", district: "", address: "" },
    transport_public_regular: "", // yes | no  (mandatory)
    cared_person: { name: "", age: null, sex: "" },
    cared_person_2: { name: "", age: null, sex: "" },
    has_second_beneficiary: false,
    service: { service_types: [], urgency: "", needs: [], notes: "" },
    technical_meeting_accepted: null, // true | false | null
    technical_meeting_refuse_reason: "",
    scheduled_qt_date: "",  // YYYY-MM-DD (só quando aceite)
    scheduled_qt_slot: "",  // HH:MM (só quando aceite)
    custom_data: {}, // valores dos custom_steps (chave = field.key)
  });

  // Load script config (custom_steps + active toggle)
  const [scriptConfig, setScriptConfig] = useState({ active: true, custom_steps: [] });
  useEffect(() => {
    api.get("/script/config").then((r) => setScriptConfig(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (scriptConfig.active === false) {
      toast.error("O Guião de Atendimento está desativado. Contacte o admin.");
      nav("/dashboard");
    }
  }, [scriptConfig.active, nav]);

  // Load existing lead if editing
  const reloadRawLead = useCallback(async () => {
    if (!existingLeadId) return;
    try {
      const r = await api.get(`/leads/${existingLeadId}`);
      setRawLead(r.data);
      return r.data;
    } catch (_e) {
      return null;
    }
  }, [existingLeadId]);

  useEffect(() => {
    if (!existingLeadId) return;
    api.get(`/leads/${existingLeadId}`).then((r) => {
      setRawLead(r.data);
      const q = r.data.qualification || {};
      const nn = (v) => v ?? "";
      setData({
        contact: {
          name: nn(q.contact?.name), phone: nn(q.contact?.phone),
          email: nn(q.contact?.email), relationship: nn(q.contact?.relationship),
          source: nn(q.contact?.source) || "Telefone", language: q.contact?.language || "pt",
          treatment: nn(q.contact?.treatment), is_self: !!q.contact?.is_self,
        },
        location: {
          postal_code: nn(q.location?.postal_code), municipality: nn(q.location?.municipality),
          district: nn(q.location?.district), address: nn(q.location?.address),
        },
        transport_public_regular: nn(q.service?.transport_public_regular),
        cared_person: {
          name: nn(q.cared_person?.name), age: q.cared_person?.age ?? null, sex: nn(q.cared_person?.sex),
        },
        cared_person_2: {
          name: nn(q.cared_person_2?.name), age: q.cared_person_2?.age ?? null, sex: nn(q.cared_person_2?.sex),
        },
        has_second_beneficiary: !!q.has_second_beneficiary,
        service: {
          service_types: q.service?.service_types || [], urgency: nn(q.service?.urgency),
          needs: q.service?.needs || [], notes: nn(q.service?.notes),
          // 06/08/2026 — Preservar campos temporais e de horário do
          // service para não os perdermos no PATCH (o backend agora
          // faz deep-merge, mas mesmo assim é mais seguro enviar
          // sempre o valor completo). Sem isto, um lead pontual
          // reeditado no guião perdia start_date/end_date/continuity.
          start_date: nn(q.service?.start_date),
          end_date: nn(q.service?.end_date),
          schedule: nn(q.service?.schedule),
          days_per_week: q.service?.days_per_week ?? null,
          hours_per_day: q.service?.hours_per_day ?? null,
          continuity: nn(q.service?.continuity),
          people_count: q.service?.people_count ?? 1,
          has_current_caregiver: q.service?.has_current_caregiver ?? null,
        },
        technical_meeting_accepted: r.data.technical_meeting_accepted ?? null,
        technical_meeting_refuse_reason: nn(r.data.technical_meeting_refuse_reason),
        scheduled_qt_date: (r.data.scheduled_qt_at || "").slice(0, 10),
        scheduled_qt_slot: (r.data.scheduled_qt_at || "").length >= 16 ? r.data.scheduled_qt_at.slice(11, 16) : "",
        custom_data: q.custom_data || {},
      });
    }).finally(() => setLoadingLead(false));
  }, [existingLeadId]);

  // Countdown
  useEffect(() => {
    const id = setInterval(() => setRemaining((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (remaining === 60 && !expiredWarned) {
      toast.warning("Falta 1 minuto para concluir a qualificação!", { duration: 4000 });
    }
    if (remaining === 0 && !expiredWarned) {
      toast.error("Tempo esgotado — finalize com o essencial ou marque um retorno.", { duration: 6000 });
      setExpiredWarned(true);
    }
  }, [remaining, expiredWarned]);

  const timerState = useMemo(() => {
    if (remaining === 0) return "expired";
    if (remaining <= 60) return "danger";
    if (remaining <= 120) return "warn";
    return "ok";
  }, [remaining]);

  const setC = (obj, k, v) => setData((d) => ({ ...d, [obj]: { ...d[obj], [k]: v } }));
  const toggleInList = (obj, k, item) => setData((d) => {
    const list = d[obj][k] || [];
    return { ...d, [obj]: { ...d[obj], [k]: list.includes(item) ? list.filter(x => x !== item) : [...list, item] } };
  });

  // Steps definition (kept inline for clarity)
  const builtinSteps = [
    { key: "identify", kicker: "Identificação", label: "1 · Contacto" },
    { key: "location", kicker: "Localização", label: "2 · Onde vive", mandatory: true },
    { key: "transport", kicker: "Transporte", label: "3 · Acessibilidade", mandatory: true },
    { key: "beneficiary", kicker: "Pessoa cuidada", label: "4 · Beneficiário" },
    { key: "service", kicker: "Contexto do pedido", label: "5 · Necessidade" },
    { key: "notes", kicker: "Notas do atendimento", label: "6 · Notas" },
    { key: "technical_meeting", kicker: "Reunião Técnica", label: "7 · Agendar" },
  ];
  const customSteps = (scriptConfig.custom_steps || []).map((s, i) => ({
    ...s,
    label: `${builtinSteps.length + i + 1} · ${s.label || s.kicker}`,
    __custom: true,
  }));
  const steps = [...builtinSteps, ...customSteps];

  // Mandatory validation
  const mandatoryOk = useMemo(() => {
    const meetingOk = data.technical_meeting_accepted === true
      ? !!(data.scheduled_qt_date && data.scheduled_qt_slot)
      : data.technical_meeting_accepted === false; // recusa também é decisão válida
    return (
      !!data.contact.name.trim() &&
      !!data.contact.phone.trim() &&
      // 20/02/2026 — treatment + postal code are now hard-required.
      !!(data.contact.treatment || "").trim() &&
      !!data.location.postal_code.trim() && data.location.postal_code.trim().length >= 8 &&
      (data.transport_public_regular === "yes" || data.transport_public_regular === "no") &&
      meetingOk
    );
  }, [data]);

  const canGoNext = useMemo(() => {
    const s = steps[step];
    if (!s) return true;
    if (s.__custom) {
      // Check mandatory fields in the current custom step
      for (const f of (s.fields || [])) {
        if (f.required) {
          const v = (data.custom_data || {})[f.key];
          if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return false;
        }
      }
      return true;
    }
    if (s.key === "identify") return !!data.contact.name.trim() && !!data.contact.phone.trim() && !!(data.contact.treatment || "").trim();
    if (s.key === "location") return !!data.location.postal_code.trim() && data.location.postal_code.trim().length >= 8;
    if (s.key === "transport") return data.transport_public_regular === "yes" || data.transport_public_regular === "no";
    return true;
  }, [step, data, steps]);

  const submit = async () => {
    if (!mandatoryOk) {
      toast.error("Complete os obrigatórios: tratamento, nome, telefone, código postal e transporte.");
      return;
    }
    setSaving(true);
    const durationSec = Math.round((Date.now() - startedAt.current) / 1000);
    const cleanEmpty = (obj) => Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v === "" ? null : v])
    );
    const qualification = {
      contact: cleanEmpty(data.contact),
      cared_person: cleanEmpty(data.cared_person),
      cared_person_2: data.has_second_beneficiary ? cleanEmpty(data.cared_person_2) : null,
      has_second_beneficiary: data.has_second_beneficiary,
      location: cleanEmpty(data.location),
      service: {
        ...data.service,
        urgency: data.service.urgency || null,
        people_count: data.has_second_beneficiary ? 2 : 1,
        transport_public_regular: data.transport_public_regular,
        script_duration_seconds: durationSec,
      },
      custom_data: data.custom_data || {},
    };
    try {
      let leadId = existingLeadId;
      const scheduledQtAt = data.technical_meeting_accepted === true && data.scheduled_qt_date && data.scheduled_qt_slot
        ? `${data.scheduled_qt_date}T${data.scheduled_qt_slot}:00`
        : null;
      const leadPayload = {
        qualification,
        technical_meeting_accepted: data.technical_meeting_accepted,
        technical_meeting_refuse_reason: data.technical_meeting_refuse_reason || null,
        ...(scheduledQtAt ? { scheduled_qt_at: scheduledQtAt, status: "qt_agendada" } : {}),
      };
      if (existingLeadId) {
        await api.patch(`/leads/${existingLeadId}`, leadPayload);
      } else {
        const { data: lead } = await api.post("/leads", leadPayload);
        leadId = lead.id;
      }

      // Sempre gerar a estimativa — mesmo que o cliente tenha recusado a Reunião Técnica.
      try {
        await api.post(`/leads/${leadId}/estimate`);
      } catch (e) {
        // Non-fatal — user still lands on lead detail; can trigger manually.
        console.warn("estimate gen failed", e);
      }

      const meetingLabel = data.technical_meeting_accepted === true
        ? "· Reunião Técnica aceite" : data.technical_meeting_accepted === false
        ? "· cliente em standby" : "";
      toast.success(`Guião concluído em ${fmtTime(durationSec)} ${meetingLabel}`);
      nav(`/leads/${leadId}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao guardar o lead");
    } finally {
      setSaving(false);
    }
  };

  const stepIcon = {
    identify: <Phone className="w-4 h-4" />,
    location: <MapPin className="w-4 h-4" />,
    transport: <Bus className="w-4 h-4" />,
    beneficiary: <Users className="w-4 h-4" />,
    service: <Heart className="w-4 h-4" />,
    notes: <ClipboardCheck className="w-4 h-4" />,
    technical_meeting: <CalendarCheck className="w-4 h-4" />,
  };
  const iconFor = (s) => stepIcon[s.key] || <Sparkles className="w-4 h-4" />;

  const setCustom = (fieldKey, value) => setData((d) => ({
    ...d, custom_data: { ...(d.custom_data || {}), [fieldKey]: value },
  }));

  const cur = steps[step];

  return (
    <div className="min-h-screen bg-muted/40" data-testid="lead-script-root">
      {/* Sticky Header with Timer */}
      <div className={`sticky top-0 z-30 border-b transition-colors ${
        timerState === "expired" ? "bg-[hsl(var(--warning-bg))] border-[#DC2626]/30"
        : timerState === "danger" ? "bg-[hsl(var(--warning-bg))] border-[#F0C29B] animate-pulse-subtle"
        : timerState === "warn" ? "bg-[hsl(var(--warning-bg))] border-primary"
        : "bg-card border-border"
      }`} data-testid="script-header">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <button
            onClick={() => nav("/dashboard")}
            className="text-[13px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
            data-testid="script-exit">
            <ArrowLeft className="w-4 h-4" /> Sair
          </button>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {existingLeadId && rawLead && (
              <CallControls lead={rawLead} onChanged={reloadRawLead} />
            )}
            <div className="text-[10px] uppercase tracking-[0.22em] text-foreground font-semibold hidden sm:block">Guião de Atendimento</div>
            <div className={`inline-flex items-center gap-2 font-display text-2xl tracking-tight px-4 py-1.5 rounded-full ${
              timerState === "expired" ? "text-[hsl(var(--danger))]"
              : timerState === "danger" ? "text-[hsl(var(--danger))]"
              : timerState === "warn" ? "text-[hsl(var(--warning))]"
              : "text-foreground"
            }`} data-testid="script-timer">
              <Timer className={`w-5 h-5 ${timerState !== "ok" ? "animate-pulse" : ""}`} />
              <span className="tabular-nums">{fmtTime(remaining)}</span>
            </div>
          </div>
        </div>
        {/* Progress dots */}
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pb-3 flex items-center gap-1.5" data-testid="script-progress">
          {steps.map((s, i) => (
            <button
              key={s.key}
              onClick={() => setStep(i)}
              data-testid={`script-step-${s.key}`}
              className={`flex-1 h-1.5 rounded-full transition-colors ${
                i === step ? "bg-foreground"
                : i < step ? "bg-primary"
                : "bg-muted"
              }`}
              title={s.label}
            />
          ))}
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-6 lg:p-8 space-y-5">
        {/* Step title */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground">
            {iconFor(cur)}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{cur.label}</div>
            <h2 className="font-display text-2xl lg:text-3xl tracking-tight text-foreground">{cur.kicker}</h2>
          </div>
        </div>

        {/* --- STEP CONTENT --- */}
        {cur.key === "identify" && (
          <ScriptBlock index={1} total={steps.length} kicker="Identificação do contacto"
            question={<>"Bom dia, aqui é a Caring, com quem falo? E o seu <b>melhor contacto telefónico</b>?"</>}
            hint="Ao pedir o nome, capture também o tratamento correto (Sr., Sra., Dr.) e confirme se o próprio interlocutor será o beneficiário do serviço.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Tratamento *">
                <select data-testid="s-contact-treatment" className={inp} value={data.contact.treatment} onChange={(e)=>setC("contact","treatment",e.target.value)}>
                  <option value="">—</option>
                  {TREATMENTS.map(t => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Nome do interlocutor *"><input data-testid="s-contact-name" className={inp} value={data.contact.name} onChange={(e)=>setC("contact","name",e.target.value)} placeholder="Ex: Ana Ferreira" autoFocus /></Field>
              <Field label="Telemóvel *"><input data-testid="s-contact-phone" className={inp} value={data.contact.phone} onChange={(e)=>setC("contact","phone",e.target.value)} placeholder="9XX XXX XXX" /></Field>
              <Field label="Email (opcional)"><input data-testid="s-contact-email" className={inp} value={data.contact.email} onChange={(e)=>setC("contact","email",e.target.value)} placeholder="nome@dominio.pt" /></Field>
              <Field label="Relação com quem vai ser cuidado">
                <select data-testid="s-contact-rel" className={inp} value={data.contact.relationship} onChange={(e)=>{
                  const val = e.target.value;
                  setData((d) => ({ ...d, contact: { ...d.contact, relationship: val, is_self: val === "Próprio" } }));
                }}>
                  <option value="">—</option>{RELATIONSHIPS.map(r=><option key={r}>{r}</option>)}
                </select>
              </Field>
              <Field label="Idioma da comunicação">
                <select data-testid="s-contact-language" className={inp}
                  value={data.contact.language || "pt"}
                  onChange={(e)=>setC("contact","language",e.target.value)}>
                  <option value="pt">Português</option>
                  <option value="en">English</option>
                </select>
              </Field>
            </div>
            {data.contact.language === "en" && (
              <div className="bg-[hsl(var(--info-bg))] border border-primary/25 rounded-lg px-3 py-2 text-[12px] text-foreground flex items-start gap-2" data-testid="s-lang-en-note">
                <span className="text-primary">🇬🇧</span>
                <span>A proposta, o email de envio e a página pública serão em <b>Inglês</b>. O Plano de Cuidados operacional (equipa) mantém-se em Português.</span>
              </div>
            )}
            {data.contact.is_self && (
              <div className="bg-[hsl(var(--success-bg))] border border-[#059669]/30 rounded-lg px-3 py-2 text-[12px] text-foreground flex items-start gap-2" data-testid="s-self-note">
                <span className="text-[#059669]">✓</span>
                <span>O interlocutor é o <b>próprio beneficiário</b> — comunicações e documentos serão dirigidos em <b>segunda pessoa</b>, sem referir "família" ou terceiros.</span>
              </div>
            )}
          </ScriptBlock>
        )}

        {cur.key === "location" && (
          <ScriptBlock index={2} total={steps.length} kicker="Localização do serviço" mandatory
            question={<>"Onde vive a pessoa que precisa de apoio? Diga-me o <b>concelho e o código postal</b>, por favor."</>}
            hint="Obrigatório — sem localização não conseguimos verificar a cobertura e o preço. Se o cliente hesitar, peça pelo menos o concelho.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Concelho *"><input data-testid="s-loc-mun" className={inp} value={data.location.municipality} onChange={(e)=>setC("location","municipality",e.target.value)} placeholder="Ex: Lisboa, Coimbra…" /></Field>
              <Field label="Código postal *">
                <input
                  data-testid="s-loc-cp"
                  className={inp}
                  value={data.location.postal_code}
                  placeholder="0000-000"
                  onChange={(e) => {
                    const cp = _normaliseCp(e.target.value);
                    setC("location", "postal_code", cp);
                    if (cp.length === 8) lookupPostal(cp);
                  }}
                  onBlur={(e) => lookupPostal(e.target.value)}
                />
                {postalLookup.loading && <div className="mt-1.5 text-[11px] text-muted-foreground">A consultar geoapi.pt…</div>}
                {postalLookup.error && <div className="mt-1.5 text-[11px] text-[hsl(var(--danger))]">{postalLookup.error}</div>}
                {postalLookup.ok && <div className="mt-1.5 text-[11px] text-[hsl(var(--success))]">Concelho e distrito preenchidos automaticamente ✓</div>}
              </Field>
              <Field label="Distrito"><input data-testid="s-loc-dist" className={inp} value={data.location.district} onChange={(e)=>setC("location","district",e.target.value)} placeholder="Ex: Lisboa" /></Field>
              <Field label="Morada (opcional)"><input data-testid="s-loc-addr" className={inp} value={data.location.address} onChange={(e)=>setC("location","address",e.target.value)} placeholder="Rua e número" /></Field>
            </div>
          </ScriptBlock>
        )}

        {cur.key === "transport" && (
          <ScriptBlock index={3} total={steps.length} kicker="Transporte público regular" mandatory
            question={<>"A zona da habitação tem <b>transportes públicos regulares</b> (autocarro, comboio ou metro a menos de 15 minutos a pé)?"</>}
            hint="Obrigatório — é crítico para o recrutamento da Ajudante Familiar. Se o cliente não souber, sugira que verifique agora no telemóvel.">
            <div className="flex flex-wrap gap-3" data-testid="s-transport">
              {[
                { v: "yes", label: "Sim, tem transportes regulares", tone: "sage" },
                { v: "no", label: "Não / não sei / distante", tone: "warn" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  onClick={() => setData((d) => ({ ...d, transport_public_regular: opt.v }))}
                  data-testid={`s-transport-${opt.v}`}
                  className={`flex-1 min-w-[220px] text-left border rounded-2xl px-5 py-4 transition-colors ${
                    data.transport_public_regular === opt.v
                      ? opt.v === "yes"
                        ? "border-[#059669] bg-[hsl(var(--success-bg))]"
                        : "border-[#DC2626] bg-[hsl(var(--warning-bg))]"
                      : "border-border bg-card hover:border-primary"
                  }`}>
                  <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${
                    data.transport_public_regular === opt.v
                      ? opt.v === "yes" ? "text-[#059669]" : "text-[hsl(var(--danger))]"
                      : "text-muted-foreground"
                  }`}>{opt.v === "yes" ? "Boa acessibilidade" : "Zona menos acessível"}</div>
                  <div className="text-[15px] mt-1 text-foreground">{opt.label}</div>
                </button>
              ))}
            </div>
            {data.transport_public_regular === "no" && (
              <div className="bg-[hsl(var(--warning-bg))] border border-primary rounded-lg px-3 py-2 text-[12px] text-[hsl(var(--warning))] flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>Alerte o cliente: em zonas com fraca acessibilidade, o recrutamento pode ser mais longo. Registe se a família disponibiliza transporte na etapa 5.</span>
              </div>
            )}
          </ScriptBlock>
        )}

        {cur.key === "beneficiary" && (
          <ScriptBlock index={4} total={steps.length} kicker="Pessoa a ser cuidada"
            question={<>"Fale-me sobre a pessoa que precisa de apoio — <b>nome, idade e sexo</b>. É apenas uma pessoa ou o casal?"</>}
            hint="Se forem duas pessoas na mesma habitação, ative o toggle e preencha as duas.">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Nome"><input data-testid="s-cp-name" className={inp} value={data.cared_person.name} onChange={(e)=>setC("cared_person","name",e.target.value)} placeholder="Ex: Manuel Ferreira" /></Field>
              <Field label="Idade"><input data-testid="s-cp-age" type="number" className={inp} value={data.cared_person.age || ""} onChange={(e)=>setC("cared_person","age", parseInt(e.target.value)||null)} /></Field>
              <Field label="Sexo">
                <select data-testid="s-cp-sex" className={inp} value={data.cared_person.sex} onChange={(e)=>setC("cared_person","sex",e.target.value)}>
                  <option value="">—</option><option value="F">Feminino</option><option value="M">Masculino</option><option value="O">Outro</option>
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer pt-2" data-testid="s-toggle-b2">
              <input type="checkbox" checked={data.has_second_beneficiary} className="accent-primary w-4 h-4"
                onChange={(e) => setData((d) => ({ ...d, has_second_beneficiary: e.target.checked }))} />
              <span>São <b>duas pessoas</b> a serem cuidadas na mesma habitação.</span>
            </label>
            {data.has_second_beneficiary && (
              <div className="border-t border-border pt-4 mt-2" data-testid="s-b2-block">
                <div className="text-[11px] uppercase tracking-[0.18em] text-foreground font-semibold mb-3">Beneficiário 2</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="Nome"><input data-testid="s-cp2-name" className={inp} value={data.cared_person_2.name} onChange={(e)=>setC("cared_person_2","name",e.target.value)} /></Field>
                  <Field label="Idade"><input data-testid="s-cp2-age" type="number" className={inp} value={data.cared_person_2.age || ""} onChange={(e)=>setC("cared_person_2","age", parseInt(e.target.value)||null)} /></Field>
                  <Field label="Sexo">
                    <select data-testid="s-cp2-sex" className={inp} value={data.cared_person_2.sex} onChange={(e)=>setC("cared_person_2","sex",e.target.value)}>
                      <option value="">—</option><option value="F">Feminino</option><option value="M">Masculino</option><option value="O">Outro</option>
                    </select>
                  </Field>
                </div>
              </div>
            )}
          </ScriptBlock>
        )}

        {cur.key === "service" && (
          <ScriptBlock index={5} total={steps.length} kicker="Que tipo de apoio procura?"
            question={<>"Que tipo de serviço procura, com que <b>urgência</b> e quais as principais necessidades? Higiene, medicação, companhia?"</>}
            hint="Múltipla escolha nos chips. Se estiver indeciso, marque 'Continuado' e 'Higiene' — os mais frequentes.">
            <Field label="Tipo de serviço">
              <div className="flex flex-wrap gap-2">
                {SERVICE_TAGS.map((s) => (
                  <button key={s.id || s.value} data-testid={`s-svc-${s.value}`} onClick={()=>toggleInList("service","service_types",s.value)} className={pill(data.service.service_types.includes(s.value))}>{s.label || s.value}</button>
                ))}
              </div>
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end pt-1">
              <Field label="Urgência">
                <div className="flex flex-wrap gap-2">
                  {URGENCIES.map(u => (
                    <button key={u.value} data-testid={`s-urg-${u.value}`} onClick={()=>setC("service","urgency",u.value)} className={pill(data.service.urgency === u.value)}>{u.label}</button>
                  ))}
                </div>
              </Field>
            </div>
            <Field label="Necessidades principais">
              <div className="flex flex-wrap gap-2">
                {NEEDS_TAGS.map((n) => (
                  <button key={n.id || n.value} data-testid={`s-need-${n.value}`} onClick={()=>toggleInList("service","needs",n.value)} className={pill(data.service.needs.includes(n.value))}>{n.label || n.value}</button>
                ))}
              </div>
            </Field>
          </ScriptBlock>
        )}

        {cur.key === "notes" && (
          <ScriptBlock index={6} total={steps.length} kicker="Notas do atendimento"
            question={<>"Há mais alguma coisa importante que eu deva saber sobre a situação?"</>}
            hint="Registe informação relevante que não coube nos campos anteriores (horário preferencial, contactos alternativos, particularidades familiares).">
            <Field label="Observações">
              <textarea data-testid="s-notes" className={ta} rows={5} value={data.service.notes} onChange={(e)=>setC("service","notes",e.target.value)} placeholder="Notas do atendimento…" />
            </Field>
          </ScriptBlock>
        )}

        {cur.key === "technical_meeting" && (
          <ScriptBlock index={7} total={steps.length} kicker="Agendar Reunião Técnica"
            question={<>"{data.contact.treatment ? data.contact.treatment + " " : ""}<b>{data.contact.name || "cliente"}</b>, para prosseguirmos com a estimativa personalizada, gostaríamos de agendar uma <b>Reunião Técnica</b> com a nossa Gestora de Cliente da vossa área. Nesta reunião ela avalia o contexto {data.contact.is_self ? "consigo" : "no domicílio"} e apresenta o plano de cuidados. <b>Aceita agendar?</b>"</>}
            hint="Se aceitar, a estimativa gera-se de imediato e o Gestor entra em contacto. Se recusar, a estimativa continua a ser gerada e enviada, mas o lead fica em standby.">
            <div className="flex flex-wrap gap-3" data-testid="s-meeting">
              <button
                onClick={() => setData((d) => ({ ...d, technical_meeting_accepted: true, technical_meeting_refuse_reason: "" }))}
                data-testid="s-meeting-accept"
                className={`flex-1 min-w-[220px] text-left border rounded-2xl px-5 py-4 transition-colors ${
                  data.technical_meeting_accepted === true
                    ? "border-[#059669] bg-[hsl(var(--success-bg))]"
                    : "border-border bg-card hover:border-primary"
                }`}>
                <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold flex items-center gap-1.5 ${
                  data.technical_meeting_accepted === true ? "text-[#059669]" : "text-muted-foreground"
                }`}><CalendarCheck className="w-3.5 h-3.5" /> Cliente aceitou</div>
                <div className="text-[15px] mt-1 text-foreground font-medium">Sim, agendar Reunião Técnica</div>
                <div className="text-[12px] text-muted-foreground mt-1">Gestor de Cliente contactá-lo-á brevemente.</div>
              </button>
              <button
                onClick={() => setData((d) => ({ ...d, technical_meeting_accepted: false }))}
                data-testid="s-meeting-refuse"
                className={`flex-1 min-w-[220px] text-left border rounded-2xl px-5 py-4 transition-colors ${
                  data.technical_meeting_accepted === false
                    ? "border-[#DC2626] bg-[hsl(var(--warning-bg))]"
                    : "border-border bg-card hover:border-primary"
                }`}>
                <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold flex items-center gap-1.5 ${
                  data.technical_meeting_accepted === false ? "text-[hsl(var(--danger))]" : "text-muted-foreground"
                }`}><XCircle className="w-3.5 h-3.5" /> Cliente recusou</div>
                <div className="text-[15px] mt-1 text-foreground font-medium">Ainda não / prefere pensar</div>
                <div className="text-[12px] text-muted-foreground mt-1">Estimativa é gerada e enviada; lead fica em standby.</div>
              </button>
            </div>

            {data.technical_meeting_accepted === true && (
              <div className="space-y-3" data-testid="s-meeting-accept-note">
                <div className="bg-[hsl(var(--success-bg))] border border-[#059669]/30 rounded-lg px-4 py-3 text-[13px] text-foreground">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#059669] font-semibold mb-1">Diga ao cliente</div>
                  <span className="italic">"Perfeito. Vamos agendar já a <b>Reunião Técnica</b> com a Gestora da sua área. Que dia e período lhe fica melhor?"</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr] gap-3">
                  <Field label="Data pretendida *">
                    <input
                      type="date"
                      data-testid="s-meeting-date"
                      className={inp}
                      min={new Date().toISOString().slice(0,10)}
                      value={data.scheduled_qt_date}
                      onChange={(e) => setData((d) => ({ ...d, scheduled_qt_date: e.target.value }))}
                    />
                  </Field>
                  <Field label="Período *">
                    <div className="flex flex-wrap gap-2" data-testid="s-meeting-slot">
                      {[
                        { v: "09:30", label: "Manhã (9h30)" },
                        { v: "11:30", label: "Meio-dia (11h30)" },
                        { v: "14:30", label: "Início tarde (14h30)" },
                        { v: "16:30", label: "Fim tarde (16h30)" },
                        { v: "18:00", label: "Após 18h" },
                      ].map((s) => (
                        <button
                          key={s.v}
                          type="button"
                          data-testid={`s-meeting-slot-${s.v}`}
                          onClick={() => setData((d) => ({ ...d, scheduled_qt_slot: s.v }))}
                          className={pill(data.scheduled_qt_slot === s.v)}>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </Field>
                </div>
                {(!data.scheduled_qt_date || !data.scheduled_qt_slot) && (
                  <div className="text-[12px] text-[hsl(var(--warning))] bg-[hsl(var(--warning-bg))] border border-primary rounded-lg px-3 py-2 flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5" />
                    <span>Escolha data e período para concluir o agendamento — o gestor receberá a Reunião Técnica já no calendário.</span>
                  </div>
                )}
              </div>
            )}
            {data.technical_meeting_accepted === false && (
              <Field label="Motivo (opcional)">
                <textarea
                  data-testid="s-meeting-reason"
                  className={ta}
                  rows={3}
                  value={data.technical_meeting_refuse_reason}
                  onChange={(e)=>setData((d)=>({ ...d, technical_meeting_refuse_reason: e.target.value }))}
                  placeholder="Ex: só quer valores por email · quer pensar · tem outro serviço em consideração…"
                />
              </Field>
            )}

            {/* Summary — mandatory validation preview */}
            <div className={`rounded-xl px-4 py-3 border mt-2 ${mandatoryOk ? "bg-muted/40 border-primary/40" : "bg-[hsl(var(--warning-bg))] border-[#DC2626]/30"}`} data-testid="s-summary">
              <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold ${mandatoryOk ? "text-foreground" : "text-[hsl(var(--danger))]"}`}>
                {mandatoryOk ? "Pronto — a estimativa será gerada automaticamente" : "Ainda falta preencher"}
              </div>
              <ul className="text-[12px] text-[#374151] mt-2 space-y-0.5">
                <li>· Nome & telefone: <b>{data.contact.name && data.contact.phone ? "✓" : "—"}</b></li>
                <li>· Localização: <b>{(data.location.municipality || data.location.postal_code) ? "✓" : "—"}</b></li>
                <li>· Transporte público regular: <b>{data.transport_public_regular === "yes" ? "Sim" : data.transport_public_regular === "no" ? "Não" : "—"}</b></li>
                <li>· Beneficiário{data.has_second_beneficiary ? "s" : ""}: <b>{data.cared_person.name || data.cared_person_2.name || "—"}</b></li>
                <li>· Tipo de serviço: <b>{data.service.service_types.length ? data.service.service_types.join(", ") : "—"}</b></li>
                <li>· Reunião Técnica: <b>{data.technical_meeting_accepted === true ? "Aceitou" : data.technical_meeting_accepted === false ? "Recusou" : "—"}</b></li>
              </ul>
            </div>
          </ScriptBlock>
        )}

        {cur.__custom && (
          <ScriptBlock index={step + 1} total={steps.length} kicker={cur.kicker}
            question={<span dangerouslySetInnerHTML={{ __html: cur.question || "" }} />}
            hint={cur.hint}
            mandatory={cur.mandatory}>
            <div className="space-y-4" data-testid={`custom-step-${cur.key}`}>
              {(cur.fields || []).map((f) => (
                <Field key={f.id || f.key} label={<>{f.label}{f.required && " *"}</>}>
                  <CustomFieldRow field={f} value={(data.custom_data || {})[f.key]} onChange={setCustom} />
                  {f.helper && <div className="text-[11px] text-muted-foreground mt-1">{f.helper}</div>}
                </Field>
              ))}
              {(cur.fields || []).length === 0 && (
                <div className="text-[12px] text-muted-foreground/70">Este passo ainda não tem campos configurados.</div>
              )}
            </div>
          </ScriptBlock>
        )}

        {/* --- NAV --- */}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            data-testid="script-prev"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground disabled:opacity-40">
            <ArrowLeft className="w-4 h-4" /> Anterior
          </button>
          {step < steps.length - 1 ? (
            <button
              onClick={() => canGoNext && setStep((s) => Math.min(steps.length - 1, s + 1))}
              disabled={!canGoNext}
              data-testid="script-next"
              className="inline-flex items-center gap-2 bg-foreground hover:bg-slate-800 disabled:bg-[#CBD5E1] disabled:cursor-not-allowed text-background font-semibold rounded-full px-6 py-3 text-sm transition-colors">
              Próxima etapa <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!mandatoryOk || saving}
              data-testid="script-submit"
              className="inline-flex items-center gap-2 bg-foreground hover:bg-foreground/90 disabled:bg-[#CBD5E1] disabled:cursor-not-allowed text-background font-semibold rounded-full px-6 py-3 text-sm transition-colors">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar lead
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
